import ClientSideValidations from '@client-side-validations/client-side-validations'
import '@client-side-validations/simple-form/dist/simple-form.bootstrap4.esm'

// simple_nested_form_for uses NestedForm::SimpleBuilder; CSV only registers SimpleForm::FormBuilder
const simpleFormBuilder = ClientSideValidations.formBuilders['SimpleForm::FormBuilder']
if (simpleFormBuilder) {
  ClientSideValidations.formBuilders['NestedForm::SimpleBuilder'] = simpleFormBuilder
}

window.ClientSideValidations = ClientSideValidations

// The package auto-start is skipped under Webpack (its isCommonJS() check is compiled
// to always be true). Enable forms ourselves.
ClientSideValidations.start()

function inputsForName(form, name) {
  const el = form.elements.namedItem(name)
  if (el) {
    if (el instanceof RadioNodeList || (typeof el.length === 'number' && el.tagName == null)) {
      return Array.from(el)
    }
    return [el]
  }

  // namedItem can miss bracketed names in some browsers; fall back to query.
  const fallback = form.querySelector(`[name="${CSS.escape(name)}"]`)
  return fallback ? [fallback] : []
}

function isVisible(element) {
  return Boolean(element.offsetWidth || element.offsetHeight || element.getClientRects().length)
}

// CSV's form enable() only binds listeners on visible inputs. Password fields in
// a hidden Bootstrap tab are skipped on load; always bind before validating so
// submit/focusout work after the tab is shown (and for Flatpickr hidden originals).
function ensureInputEnabled(input) {
  if (input.disabled) return
  ClientSideValidations.enablers.input(input)
}

function syncFlatpickrInvalidState(input, invalid) {
  const altInput = input._flatpickr?.altInput
  if (!altInput) return
  altInput.classList.toggle('is-invalid', invalid)
}

function syncInlineCalendarInvalidState(input, invalid) {
  const calendar = input.closest('.flatpickr-inline-calendar')
  if (!calendar) return
  calendar.classList.toggle('has-error', invalid)
}

function syncSelect2InvalidState(input, invalid) {
  const $input = window.jQuery?.(input)
  if (!$input?.data?.('select2')) return
  $input.next('.select2-container').toggleClass('is-invalid', invalid)
}

function syncCompositeInvalidState(input, invalid) {
  syncFlatpickrInvalidState(input, invalid)
  syncInlineCalendarInvalidState(input, invalid)
  syncSelect2InvalidState(input, invalid)
}

export function csvForm(element) {
  const form = element?.closest?.('form') || element
  if (!(form instanceof HTMLFormElement)) return null
  if (!form.hasAttribute('data-client-side-validations')) return null
  if (!window.ClientSideValidations) return null
  return form
}

export function persistCsvSettings(form) {
  form.dataset.clientSideValidations = JSON.stringify(form.ClientSideValidations.settings)
}

export function setPresenceValidator(validators, name) {
  if (!name) return

  validators[name] = {
    ...(validators[name] || {}),
    presence: [{ message: "can't be blank" }]
  }
}

function csvFieldValidators(input, validators) {
  if (!input?.name || !validators) return {}
  if (Object.prototype.hasOwnProperty.call(validators, input.name)) return validators[input.name]
  return {}
}

function csvFailMessage(input, validators) {
  const fieldValidators = csvFieldValidators(input, validators)
  const all = ClientSideValidations.validators.all()

  for (const kind of Object.keys(fieldValidators)) {
    const fn = all[kind]
    if (!fn) continue

    for (const options of fieldValidators[kind] || []) {
      const message = fn.call(all, input, options)
      if (message) return message
    }
  }

  return "can't be blank"
}

// CSV does not bind element:validate:fail/pass on ids ending in _confirmation
// (those fields only re-check the main input). When we inject presence on
// password_confirmation, apply error UI ourselves.
function syncConfirmationErrorUi(form, input, ok, validators) {
  if (!input.id?.endsWith('_confirmation') || !form.ClientSideValidations) return

  if (ok) {
    form.ClientSideValidations.removeError(input)
    return
  }

  const message = csvFailMessage(input, validators)
  if (message) form.ClientSideValidations.addError(input, message)
}

export function clearCsvError(input) {
  if (!input) return

  const htmlSettings = input.form?.ClientSideValidations?.settings?.html_settings
  const builder = htmlSettings && window.ClientSideValidations?.formBuilders?.[htmlSettings.type]
  if (builder?.remove) builder.remove(input, htmlSettings)

  syncCompositeInvalidState(input, false)
}

export function revalidateCsv(input) {
  if (!input || !window.ClientSideValidations) return

  const form = input.form
  const validators = form?.ClientSideValidations?.settings?.validators
  if (!validators?.[input.name]) return

  ClientSideValidations.enablers.input(input)
  input.dataset.csvValidate = 'true'
  input.dataset.csvChanged = 'true'
  ClientSideValidations.isValid(input, validators)
}

// Select2 hides the original <select>, so CSV never receives focusout after a
// pick — the only event that normally clears field errors. Revalidate when the
// value changes instead.
//
// 1) Native change: select2-initializer dispatches this after select/clear.
// 2) select2:* via window.jQuery: covers other Select2 init paths (Plugins, etc).
document.addEventListener('change', (event) => {
  const input = event.target
  if (!(input instanceof HTMLSelectElement)) return
  if (!input.classList.contains('select2-hidden-accessible')) return
  revalidateCsv(input)
})

function bindSelect2CsvRevalidation() {
  const $ = window.jQuery
  if (!$?.fn) {
    document.addEventListener('DOMContentLoaded', bindSelect2CsvRevalidation, { once: true })
    return
  }

  $(document)
    .off('select2:select.csv select2:clear.csv select2:unselect.csv')
    .on('select2:select.csv select2:clear.csv select2:unselect.csv', 'select', function () {
      revalidateCsv(this)
    })
}

bindSelect2CsvRevalidation()

const originalElementFail = ClientSideValidations.callbacks.element.fail
ClientSideValidations.callbacks.element.fail = (element, message, addError, eventData) => {
  originalElementFail(element, message, addError, eventData)
  syncCompositeInvalidState(element, true)
}

const originalElementPass = ClientSideValidations.callbacks.element.pass
ClientSideValidations.callbacks.element.pass = (element, removeError, eventData) => {
  originalElementPass(element, removeError, eventData)
  syncCompositeInvalidState(element, false)
}

// Validate every field listed in the form's CSV validators, including Select2-hidden
// selects and Flatpickr altInput originals that CSV's form-level isValid skips.
function csvFormIsValid(form) {
  // enable() re-parses data-client-side-validations; keep any runtime-injected
  // validators (e.g. recurring fields) that were merged into settings in memory.
  const runtimeValidators = form.ClientSideValidations?.settings?.validators
  ClientSideValidations.enable(form)
  if (runtimeValidators) {
    form.ClientSideValidations.settings.validators = {
      ...form.ClientSideValidations.settings.validators,
      ...runtimeValidators
    }
  }

  const validators = form.ClientSideValidations?.settings?.validators || {}
  let valid = true

  Object.keys(validators).forEach((name) => {
    inputsForName(form, name).forEach((input) => {
      if (input.disabled) return
      ensureInputEnabled(input)
      input.dataset.csvValidate = 'true'
      input.dataset.csvChanged = 'true'

      let ok = true
      try {
        ok = ClientSideValidations.isValid(input, validators)
      } catch (error) {
        console.error('[ClientSideValidations] isValid', error)
        ok = false
      }

      // CSV fail handlers call addError before setting csvValid; if addError
      // throws, isValid can incorrectly report true. Re-check presence only.
      if (ok) {
        const presenceOpts = csvFieldValidators(input, validators).presence
        const presenceFn = ClientSideValidations.validators.all().presence
        if (presenceFn && presenceOpts?.length) {
          for (const options of presenceOpts) {
            const message = presenceFn(input, options)
            if (message) {
              ok = false
              try {
                form.ClientSideValidations?.addError(input, message)
              } catch (error) {
                console.error('[ClientSideValidations] addError', error)
              }
              break
            }
          }
        }
      }

      syncCompositeInvalidState(input, !ok)
      syncConfirmationErrorUi(form, input, ok, validators)
      if (!ok) valid = false
    })
  })

  return valid
}

// Select2 / Flatpickr keep the named field clipped or hidden. Scrolling or
// focusing that original node can shove the page horizontally; prefer the
// visible widget instead.
function visibleCsvErrorTarget(element) {
  if (!(element instanceof Element)) return element

  if (element.classList.contains('select2-hidden-accessible')) {
    const container = element.nextElementSibling
    if (container?.classList.contains('select2-container')) return container
  }

  if (element._flatpickr?.altInput) return element._flatpickr.altInput

  const inlineCalendar = element.closest?.('.flatpickr-inline-calendar')
  if (inlineCalendar?.classList.contains('has-error')) return inlineCalendar

  return element
}

function scrollToFirstCsvError(form) {
  const raw = form.querySelector('.select2-container.is-invalid, .is-invalid, .has-error, .invalid-feedback')
  if (!raw) return

  const target = visibleCsvErrorTarget(raw)
  target.scrollIntoView({ block: 'center', behavior: 'smooth' })

  let focusable
  if (target.classList?.contains('select2-container')) {
    focusable = target.querySelector('.select2-selection')
  } else if (target.matches?.('input, select, textarea, button')) {
    focusable = target
  } else {
    focusable = target.querySelector?.('input:not(.select2-hidden-accessible), select:not(.select2-hidden-accessible), textarea, button, [tabindex]:not([tabindex="-1"])')
      || target
  }

  if (focusable && typeof focusable.focus === 'function') {
    focusable.focus({ preventScroll: true })
  }
}

// Capture phase so we run before Turbo's document submit handlers.
document.addEventListener(
  'submit',
  (event) => {
    const form = event.target
    if (!(form instanceof HTMLFormElement)) return
    if (!form.hasAttribute('data-client-side-validations')) return

    try {
      if (!csvFormIsValid(form)) {
        event.preventDefault()
        event.stopImmediatePropagation()
        scrollToFirstCsvError(form)
      }
    } catch (error) {
      console.error('[ClientSideValidations]', error)
      event.preventDefault()
      event.stopImmediatePropagation()
    }
  },
  true
)

// Password (and other) CSV forms inside Bootstrap tabs are invisible on first
// enable(); re-bind when a tab is shown so focusout validation works too.
document.addEventListener('shown.bs.tab', (event) => {
  const paneSelector = event.target?.getAttribute?.('data-bs-target')
  if (!paneSelector) return
  const pane = document.querySelector(paneSelector)
  if (!pane) return

  pane.querySelectorAll(ClientSideValidations.selectors.forms).forEach((form) => {
    ClientSideValidations.enable(form)
  })
})

export default ClientSideValidations
